/**
 * Which stores and which people a pay report covers (plan 0031, owner 2026-10-06). A manager with
 * several stores, such as a franchisee whose staff work shifts in all of them, picks the stores whose
 * hours go in and the people whose pay it is; the accountant then gets one file per employer.
 * The stores are remembered per store in this browser, so next month's report is one click. The people
 * are in the page address since 2026-11-04 (owner), `?personer=7,12`, so a refresh or a shared link
 * keeps them: ids only, anything else in the list is dropped, and an id with no pay in the report is
 * ignored (`within`). No `personer` is everyone; `personer=` is nobody.
 */
import type { ReportRow } from '@/api/data';
import { T } from '@/i18n';
import type { Tenant } from '@/shell/tenant';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

interface Saved {
  stores: number[];
}

/** The chosen people from `?personer=`: null (everyone) when absent, else the distinct whole-number ids. */
export function peopleFrom(raw: string | null): string[] | null {
  if (raw == null) return null;
  return [
    ...new Set(
      raw
        .split(',')
        .map((x) => x.trim())
        .filter((x) => /^\d{1,9}$/.test(x)),
    ),
  ];
}

const KEY = (t: number) => `timefly.lonn.scope.${t}`;
function load(t: number | undefined): Saved | null {
  if (t == null) return null;
  try {
    const v = JSON.parse(localStorage.getItem(KEY(t)) ?? 'null');
    return v && Array.isArray(v.stores) ? v : null;
  } catch {
    return null;
  }
}
function save(t: number | undefined, v: Saved) {
  if (t == null) return;
  try {
    localStorage.setItem(KEY(t), JSON.stringify(v));
  } catch {
    /* private window: the choice lasts this visit */
  }
}

export interface Scope {
  /** the chosen stores, the active one included unless unticked */
  stores: number[];
  people: string[] | null;
  /** more than the active store: hours come from other stores too */
  multi: boolean;
  /** for GET /admin/report: '' or '&stores=1,4' */
  query: string;
  /** the same, plus the people, for the two files */
  fileQuery: string;
  setStores: (ids: number[]) => void;
  setPeople: (ids: string[] | null) => void;
  /** the rows for the chosen people */
  pick: (rows: ReportRow[]) => ReportRow[];
  /** the same scope with the people narrowed to these ids (the report's, once read): an unknown id is ignored */
  within: (ids: string[] | null) => Scope;
}

export function useScope(active: number | undefined, tenants: Tenant[]): Scope {
  const [saved, setSaved] = useState<Saved | null>(() => load(active));
  const [forStore, setForStore] = useState(active);
  const [sp, setSp] = useSearchParams();
  // another store became active: its own remembered choice
  if (forStore !== active) {
    setForStore(active);
    setSaved(load(active));
  }
  const known = new Set(tenants.map((t) => t.id));
  const stores = (saved?.stores ?? []).filter((id) => known.has(id) || id === active);
  const chosen = stores.length ? stores : active != null ? [active] : [];
  const multi = chosen.length > 1 || (chosen.length === 1 && chosen[0] !== active);
  const query = multi ? `&stores=${chosen.join(',')}` : '';
  const setPeople = (ids: string[] | null) =>
    setSp(
      (n) => {
        if (ids == null) n.delete('personer');
        else n.set('personer', ids.join(','));
        n.delete('side');
        return n;
      },
      { replace: true },
    );
  const make = (people: string[] | null): Scope => ({
    stores: chosen,
    people,
    multi,
    query,
    fileQuery: `${query}${people ? `&people=${people.join(',')}` : ''}`,
    setStores: (ids) => {
      setSaved({ stores: ids });
      save(active, { stores: ids });
    },
    setPeople,
    pick: (rows) => (people ? rows.filter((r) => people.includes(String(r.id))) : rows),
    within: (ids) => (people && ids ? make(people.filter((p) => ids.includes(p))) : make(people)),
  });
  return make(peopleFrom(sp.get('personer')));
}

/**
 * The stores as ticks, in the side panel "2 bedrifter" opens (manager redesign, 2026-10-09). At least
 * one stays ticked: an empty store list would be the active store again, silently.
 */
export function StoresPick({ scope, tenants }: { scope: Scope; tenants: Tenant[] }) {
  const toggle = (id: number) => {
    const next = scope.stores.includes(id) ? scope.stores.filter((x) => x !== id) : [...scope.stores, id];
    if (next.length) scope.setStores(next);
  };
  return (
    <fieldset className="lpick" data-testid="scope-stores">
      <legend>{T('Bedrifter i lønnsgrunnlaget')}</legend>
      {tenants.map((t) => (
        <label key={t.id}>
          <input
            type="checkbox"
            checked={scope.stores.includes(t.id)}
            disabled={scope.stores.length === 1 && scope.stores.includes(t.id)}
            onChange={() => toggle(t.id)}
          />{' '}
          {t.name}
        </label>
      ))}
      <button type="button" className="mlink" onClick={() => scope.setStores(tenants.map((t) => t.id))}>
        {T('Alle mine bedrifter')}
      </button>
      {scope.multi && (
        <p className="lnote">
          {T(
            'Timene fra alle valgte butikker er lagt sammen per person, og hver vakt er priset etter butikken den ble jobbet i. Overtid telles over uken og dagen i alle valgte butikker, med overtidsregelen til butikken timen ble jobbet i.',
          )}
        </p>
      )}
    </fieldset>
  );
}

/** The people with pay in the chosen stores. None ticked is nobody, on the screen and in the files. */
export function PeoplePick({
  scope,
  rows,
  nameOf,
}: {
  scope: Scope;
  rows: ReportRow[];
  /** the name as the table shows it; the report row carries it as stored (#274 N14) */
  nameOf?: (id: string) => string | undefined;
}) {
  const everyone = rows.map((r) => String(r.id));
  const ticked = scope.people ?? everyone;
  const toggle = (id: string) => {
    const next = ticked.includes(id) ? ticked.filter((x) => x !== id) : [...ticked, id];
    scope.setPeople(next.length === everyone.length ? null : next);
  };
  return (
    <fieldset className="lpick" data-testid="scope-people">
      <legend>{T('Ansatte i lønnsgrunnlaget')}</legend>
      <span className="lpickall">
        <button type="button" className="mlink" onClick={() => scope.setPeople(null)}>
          {T('Alle')}
        </button>
        <button type="button" className="mlink" onClick={() => scope.setPeople([])}>
          {T('Ingen')}
        </button>
      </span>
      {/* the picker lists people with pay this month; the table also shows active people with none (#274 R08) */}
      <p className="lnote">{T('Bare ansatte med lønn i måneden kan velges.')}</p>
      {rows.length === 0 && <p className="lnote">{T('Ingen har lønn i måneden.')}</p>}
      {rows.map((r) => (
        <label key={r.id}>
          <input
            type="checkbox"
            checked={ticked.includes(String(r.id))}
            onChange={() => toggle(String(r.id))}
          />{' '}
          {nameOf?.(String(r.id)) ?? r.name}
        </label>
      ))}
    </fieldset>
  );
}
